import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Progress } from '../ui/progress';
import { 
  Calendar, 
  Trophy, 
  Star, 
  Clock, 
  MousePointer2, 
  Play, 
  Share2, 
  Loader2,
  Crown,
  Medal,
  ChevronLeft,
  Save,
  CheckCircle2,
  Flame,
  Swords,
  RotateCcw,
  Shuffle,
  X,
  ArrowLeft
} from 'lucide-react';
import { formatTime } from '../../lib/gameUtils';
import { cn, getEasternDateString, getTimeUntilMidnightEastern, formatDateEastern, isMobileDevice } from '../../lib/utils';
import { logger } from '../../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { isProfane, sanitizeName } from '../../lib/profanityFilter';
import { 
  getProductionUrl, 
  getDailyShareUrl, 
  getDailyShareImageUrl,
  handleMessengerShare, 
  handleInstagramShare 
} from './challengeUtils';
import { ChallengeShareActions } from './ChallengeShareActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Streak display component
function StreakDisplay({ streak, compact = false }) {
  if (!streak) return null;
  
  const { current_streak, longest_streak, badges, next_badge, streak_active } = streak;
  
  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <Flame className={cn("h-4 w-4", current_streak > 0 ? "text-orange-500" : "text-muted-foreground")} />
        <span className={cn("font-bold", current_streak > 0 ? "text-orange-500" : "text-muted-foreground")}>
          {current_streak}
        </span>
        <span className="text-muted-foreground">day streak</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {/* Current Streak */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn(
            "p-2 rounded-lg",
            current_streak > 0 ? "bg-orange-500/10" : "bg-muted"
          )}>
            <Flame className={cn("h-5 w-5", current_streak > 0 ? "text-orange-500" : "text-muted-foreground")} />
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Current Streak</div>
            <div className={cn("font-bold text-lg", current_streak > 0 ? "text-orange-500" : "text-muted-foreground")}>
              {current_streak} {current_streak === 1 ? 'day' : 'days'}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Best Streak</div>
          <div className="font-semibold">{longest_streak} days</div>
        </div>
      </div>
      
      {/* Next Badge Progress */}
      {next_badge && (
        <div className="bg-muted/30 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Next Badge</span>
            <span className="font-medium flex items-center gap-1">
              <span>{next_badge.emoji}</span>
              <span>{next_badge.name}</span>
            </span>
          </div>
          <Progress value={(current_streak / next_badge.days) * 100} className="h-2" />
          <div className="text-xs text-muted-foreground text-center">
            {next_badge.days_remaining} more {next_badge.days_remaining === 1 ? 'day' : 'days'} to go!
          </div>
        </div>
      )}
      
      {/* Earned Badges */}
      {badges && badges.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground font-medium">Badges Earned</div>
          <div className="flex flex-wrap gap-2">
            {badges.map((badge) => (
              <div
                key={badge.id}
                className="flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 border border-primary/20 text-sm"
                title={badge.description}
              >
                <span>{badge.emoji}</span>
                <span className="font-medium">{badge.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// New badge celebration component
function NewBadgeCelebration({ badge, onClose }) {
  if (!badge) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-in fade-in">
      <div className="bg-card rounded-2xl p-8 shadow-2xl max-w-sm mx-4 text-center animate-in zoom-in-95">
        <div className="text-6xl mb-4 animate-bounce">{badge.emoji}</div>
        <h3 className="text-2xl font-bold mb-2">New Badge Unlocked!</h3>
        <p className="text-lg font-semibold text-primary mb-1">{badge.name}</p>
        <p className="text-muted-foreground mb-4">{badge.description}</p>
        <Button onClick={onClose} className="w-full">Awesome!</Button>
      </div>
    </div>
  );
}

// Countdown timer component (counts down to midnight Eastern Time)
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      // Use Eastern Time for countdown
      const diff = getTimeUntilMidnightEastern();
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft({ hours, minutes, seconds });
    };
    
    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  const pad = (num) => String(num).padStart(2, '0');
  
  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-center gap-1 text-sm font-mono">
        <span className="bg-muted px-2 py-1 rounded">{pad(timeLeft.hours)}</span>
        <span>:</span>
        <span className="bg-muted px-2 py-1 rounded">{pad(timeLeft.minutes)}</span>
        <span>:</span>
        <span className="bg-muted px-2 py-1 rounded">{pad(timeLeft.seconds)}</span>
      </div>
      <span className="text-[10px] text-muted-foreground mt-1">Eastern Time</span>
    </div>
  );
}

// Leaderboard entry component with streak info
function LeaderboardEntry({ entry, rank }) {
  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />;
    if (rank === 3) return <Medal className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />;
    return <span className="text-xs sm:text-sm text-muted-foreground font-mono">#{rank}</span>;
  };
  
  const streak = entry.streak;
  const hasStreak = streak && streak.current > 0;
  const hasBadges = streak && streak.badges && streak.badges.length > 0;
  
  return (
    <div className={cn(
      "flex items-center gap-1.5 sm:gap-3 p-2 sm:p-3 rounded-lg border transition-colors w-full box-border",
      rank <= 3 ? "bg-accent/30 border-accent" : "bg-card/50 border-border/50"
    )}>
      <div className="w-5 sm:w-8 flex justify-center flex-shrink-0">
        {getRankIcon(rank)}
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="flex items-center gap-1 sm:gap-2">
          <span className="font-medium truncate text-sm sm:text-base">{entry.player_name}</span>
          {entry.perfect_game && (
            <Star className="h-3 w-3 sm:h-4 sm:w-4 text-warning fill-warning flex-shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground">
          <span className="flex items-center gap-0.5 sm:gap-1">
            <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
            {formatTime(entry.time_seconds)}
          </span>
          <span className="flex items-center gap-0.5 sm:gap-1">
            <MousePointer2 className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
            {entry.moves}
          </span>
        </div>
      </div>
      {/* Streak & Badges Column - Hidden on very small screens */}
      <div className="hidden xs:flex items-center gap-1 sm:gap-2 flex-shrink-0">
        {hasStreak && (
          <div className="flex items-center gap-0.5 sm:gap-1 text-orange-500" title={`${streak.current} day streak`}>
            <Flame className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="text-[10px] sm:text-sm font-bold">{streak.current}</span>
          </div>
        )}
        {hasBadges && (
          <div className="flex items-center -space-x-1" title={streak.badges.map(b => b.name).join(', ')}>
            {streak.badges.slice(0, 2).map((badge, i) => (
              <span key={badge.id} className="text-xs sm:text-sm" style={{ zIndex: 3 - i }}>
                {badge.emoji}
              </span>
            ))}
            {streak.badges.length > 2 && (
              <span className="text-[10px] sm:text-xs text-muted-foreground ml-1">+{streak.badges.length - 2}</span>
            )}
          </div>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-bold text-sm sm:text-lg">{Math.round(entry.score)}</div>
        <div className="text-[10px] sm:text-xs text-muted-foreground">pts</div>
      </div>
    </div>
  );
}

export function DailyChallengeModal({ 
  isOpen, 
  onClose, 
  onPlay,
  hasPlayedToday,
  todayScore,
  playerStreak,
  onChallengeFriend,
  deviceId
}) {
  const [view, setView] = useState('main'); // 'main' | 'leaderboard' | 'share' | 'streaks'
  const [dailyInfo, setDailyInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch daily info and leaderboard
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const isMobile = isMobileDevice();
      const [infoRes, leaderboardRes, badgesRes] = await Promise.all([
        axios.get(`${API}/daily/info`),
        axios.get(`${API}/daily/leaderboard?limit=40&is_mobile=${isMobile}`),
        axios.get(`${API}/daily/badges`)
      ]);
      
      setDailyInfo(infoRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setAllBadges(badgesRes.data.badges || []);
    } catch (error) {
      logger.error('Failed to fetch daily challenge data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (isOpen) {
      fetchData();
      setView('main');
    }
  }, [isOpen, fetchData]);
  
  const handlePlay = () => {
    if (dailyInfo) {
      onPlay(dailyInfo.image_url, dailyInfo.puzzle_date);
      onClose();
    }
  };
  
  // Find user's rank if they played today
  const userRank = todayScore 
    ? leaderboard.findIndex(e => e.id === todayScore.id) + 1 
    : null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-md w-[calc(var(--vvw,100vw)-1rem)] max-w-[calc(var(--vvw,100vw)-1rem)] sm:w-auto p-0 box-border overflow-y-auto overflow-x-hidden max-h-[calc(var(--vvh,100dvh)*0.9)] scrollbar-visible" 
        data-testid="daily-challenge-modal"
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : view === 'main' ? (
          /* Main View */
          <div className="flex flex-col w-full max-w-full relative overflow-hidden">
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="absolute top-2 right-2 h-8 w-8 z-20 bg-background/50 hover:bg-background/80"
              data-testid="daily-challenge-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            {/* Header with Image Preview */}
            <div className="relative h-32 sm:h-48 w-full max-w-full overflow-hidden bg-gradient-to-br from-primary/20 to-accent/20">
              {dailyInfo?.image_url && (
                <img 
                  src={dailyInfo.image_url} 
                  alt="Today's puzzle" 
                  className="absolute inset-0 w-full h-full object-cover opacity-60"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <Calendar className="h-4 w-4" />
                  {dailyInfo && formatDateEastern(dailyInfo.puzzle_date)}
                </div>
                <h2 className="text-2xl font-heading font-bold">Daily Challenge</h2>
                <p className="text-sm text-muted-foreground">Same puzzle for everyone today (Eastern Time)</p>
              </div>
            </div>
            
            {/* Content (ScrollArea gives proper containment on scaled Android displays) */}
            <ScrollArea className="w-full max-h-[calc(var(--vvh,100dvh)*0.6)] sm:max-h-[calc(var(--vvh,100dvh)*0.65)]">
              <div className="p-3 space-y-3 sm:p-4 sm:space-y-4" data-testid="daily-challenge-main-content">
              {/* Already Played Badge */}
              {hasPlayedToday && todayScore && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-success/10 border border-success/30">
                  <CheckCircle2 className="h-6 w-6 text-success flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-success">Completed Today!</div>
                    <div className="text-sm text-muted-foreground">
                      Score: <span className="font-bold text-foreground">{Math.round(todayScore.score)}</span>
                      {userRank && ` • Rank #${userRank}`}
                    </div>
                  </div>
                  {todayScore.perfect_game && (
                    <Star className="h-5 w-5 text-warning fill-warning" />
                  )}
                </div>
              )}
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">{leaderboard.length}</div>
                  <div className="text-xs text-muted-foreground">Players Today</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3 text-center">
                  <div className="text-xs text-muted-foreground mb-1">Next Puzzle In</div>
                  <CountdownTimer />
                </div>
              </div>
              
              {/* Streak Info (if player has played before) */}
              {playerStreak && (playerStreak.current_streak > 0 || playerStreak.badges?.length > 0) && (
                <div 
                  className="bg-orange-500/5 rounded-lg p-3 border border-orange-500/20 cursor-pointer hover:bg-orange-500/10 transition-colors"
                  onClick={() => setView('streaks')}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Flame className={cn("h-5 w-5", playerStreak.current_streak > 0 ? "text-orange-500" : "text-muted-foreground")} />
                      <div>
                        <div className="font-bold text-orange-500">{playerStreak.current_streak} Day Streak</div>
                        <div className="text-xs text-muted-foreground">
                          {playerStreak.badges?.length > 0 && `${playerStreak.badges.length} badge${playerStreak.badges.length > 1 ? 's' : ''} earned`}
                          {playerStreak.badges?.length === 0 && playerStreak.next_badge && `${playerStreak.next_badge.days_remaining} days to ${playerStreak.next_badge.emoji}`}
                        </div>
                      </div>
                    </div>
                    <ChevronLeft className="h-4 w-4 rotate-180 text-muted-foreground" />
                  </div>
                </div>
              )}
              
              {/* Rules */}
              <div className="bg-primary/5 rounded-lg p-3 border border-primary/20">
                <div className="text-xs font-medium text-primary mb-2">Challenge Rules</div>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-primary" />
                    Fixed <span className="font-medium text-foreground">Normal</span> difficulty for fair competition
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-primary" />
                    Everyone solves the same puzzle
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-primary" />
                    New puzzle every day at midnight
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-orange-500" />
                    <span className="text-orange-500">Build streaks</span> to earn badges!
                  </li>
                </ul>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setView('leaderboard')}
                >
                  <Trophy className="h-4 w-4 mr-2" />
                  Leaderboard
                </Button>
                {hasPlayedToday ? (
                  <Button 
                    className="flex-1 bg-gradient-to-r from-primary to-accent"
                    onClick={() => setView('share')}
                    data-testid="daily-open-share-btn"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share Score
                  </Button>
                ) : (
                  <Button 
                    className="flex-1 bg-gradient-to-r from-primary to-accent"
                    onClick={handlePlay}
                    data-testid="play-daily-challenge-btn"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Play Now
                  </Button>
                )}
              </div>
              </div>
            </ScrollArea>
          </div>
        ) : view === 'streaks' ? (
          /* Streaks View */
          <div className="flex flex-col">
            <DialogHeader className="p-4 border-b">
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setView('main')}
                  className="h-8 w-8"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <DialogTitle className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-orange-500" />
                  Streaks & Badges
                </DialogTitle>
              </div>
            </DialogHeader>
            
            <div className="p-4 space-y-4">
              {/* Current Streak Display */}
              <StreakDisplay streak={playerStreak} />
              
              {/* All Available Badges */}
              <div className="space-y-3">
                <div className="text-sm font-medium">All Badges</div>
                <div className="space-y-2">
                  {allBadges.map((badge) => {
                    const earned = playerStreak?.badges?.some(b => b.id === badge.id);
                    return (
                      <div
                        key={badge.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                          earned ? "bg-primary/5 border-primary/30" : "bg-muted/30 border-border/50 opacity-60"
                        )}
                      >
                        <div className={cn(
                          "text-2xl",
                          !earned && "grayscale"
                        )}>
                          {badge.emoji}
                        </div>
                        <div className="flex-1">
                          <div className={cn("font-medium", earned && "text-primary")}>
                            {badge.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {badge.description}
                          </div>
                        </div>
                        {earned && (
                          <CheckCircle2 className="h-5 w-5 text-success" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : view === 'leaderboard' ? (
          /* Leaderboard View */
          <div className="flex flex-col h-[500px] max-h-[calc(var(--vvh,100dvh)-4rem)] w-full max-w-full box-border">
            <DialogHeader className="p-3 sm:p-4 border-b flex-shrink-0">
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setView('main')}
                  className="h-8 w-8 flex-shrink-0"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                  <span className="truncate">Today's Leaderboard</span>
                </DialogTitle>
              </div>
            </DialogHeader>
            
            <ScrollArea className="flex-1 p-3 sm:p-4 min-h-0">
              {leaderboard.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-center">
                  <Trophy className="h-12 w-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">No scores yet today!</p>
                  <p className="text-sm text-muted-foreground">Be the first to complete the challenge.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((entry, index) => (
                    <LeaderboardEntry 
                      key={entry.id} 
                      entry={entry} 
                      rank={index + 1} 
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
            
            {!hasPlayedToday && (
              <div className="p-3 sm:p-4 border-t flex-shrink-0">
                <Button 
                  className="w-full bg-gradient-to-r from-primary to-accent"
                  onClick={handlePlay}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Play Daily Challenge
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Share View - mirrors "Score Saved!" screen */
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 relative">
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute top-3 left-3 sm:top-4 sm:left-4"
              onClick={() => setView('main')}
              data-testid="daily-share-back-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 h-8 w-8"
              data-testid="daily-share-view-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>

            <div className="text-center pt-6">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 mb-3 sm:mb-4">
                <Share2 className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h2 className="text-xl sm:text-2xl font-heading font-bold">Share Challenge</h2>
              {userRank && (
                <p className="text-base sm:text-lg mt-2">
                  You're ranked <span className="font-bold text-primary">#{userRank}</span> today!
                </p>
              )}
            </div>

            {todayScore && (
              <div className="text-center py-3 sm:py-4 bg-muted/30 rounded-xl">
                <div className="text-3xl sm:text-4xl font-heading font-bold text-gradient">
                  {Math.round(todayScore.score)}
                </div>
                <div className="text-sm text-muted-foreground">points</div>
              </div>
            )}

            <div className="space-y-2 sm:space-y-3">
              <p className="text-xs sm:text-sm text-muted-foreground text-center whitespace-pre-line">
                Share your achievement!{'\n'}and Challenge your Friends
              </p>

              <ChallengeShareActions
                imageUrl={todayScore?.image_url || dailyInfo?.image_url}
                difficulty="normal"
                scoreData={{ score: todayScore?.score, isPerfect: todayScore?.perfect_game }}
                time={todayScore?.time_seconds}
                moves={todayScore?.moves}
                deviceId={deviceId}
                playerName={todayScore?.player_name || localStorage.getItem('jigsaw_daily_player_name') || ''}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Completion modal for daily challenge
export function DailyCompletionModal({
  isOpen,
  onClose,
  scoreData,
  time,
  moves,
  puzzleDate,
  deviceId,
  onSaveAndShare,
  onChallengeFriend,
  onReplayPuzzle,
  onNewPuzzle
}) {
  const [step, setStep] = useState('congratulations');
  const [playerName, setPlayerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedScore, setSavedScore] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Synchronous double-submit guard. setIsSubmitting is async (state batches),
  // so a quick double-tap on mobile can fire two POSTs before React re-renders
  // the disabled button. A ref flips immediately and blocks the second call.
  const submitLockRef = useRef(false);
  
  useEffect(() => {
    if (isOpen) {
      setStep('congratulations');
      // Pre-fill with saved player name to maintain streak
      const savedName = sanitizeName(localStorage.getItem('jigsaw_daily_player_name') || '');
      setPlayerName(savedName);
      setIsSubmitting(false);
      setSavedScore(null);
      setSubmitError(null);
      submitLockRef.current = false;
    }
  }, [isOpen]);
  
  if (!scoreData) return null;
  
  const { score, isPerfect } = scoreData;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!playerName.trim() || isSubmitting) return;
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    if (isProfane(playerName.trim())) {
      toast.error('Profanity Detected! - Try Again');
      submitLockRef.current = false;
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const isMobile = isMobileDevice();
      const response = await axios.post(`${API}/daily/score?is_mobile=${isMobile}`, {
        player_name: playerName.trim(),
        time_seconds: time,
        moves,
        device_id: deviceId
      });
      setSavedScore(response.data);
      
      // Also save to completed puzzles history (shared table with grid_size)
      if (deviceId && response.data?.image_url) {
        try {
          await axios.post(`${API}/images/completed`, {
            device_id: deviceId,
            image_url: response.data.image_url,
            player_name: playerName.trim(),
            time_seconds: time,
            moves,
            score: score,
            difficulty: 'normal', // Daily challenges are normal difficulty
            perfect_game: isPerfect || false,
            grid_size: isMobile ? 4 : 6
          });
        } catch (historyError) {
          logger.error('Failed to save to history:', historyError);
        }
      }
      
      // Save player name for next time
      localStorage.setItem('jigsaw_daily_player_name', playerName.trim());
      localStorage.setItem('jigsaw_player_name', playerName.trim());
      
      setStep('share');
    } catch (error) {
      logger.error('Failed to submit daily score:', error);
      if (error.response?.status === 409) {
        // Device already played
        setSubmitError('This device has already submitted a score for today\'s challenge.');
      } else {
        setSubmitError('Failed to save score. Please try again.');
      }
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };
  
  const handleShare = (platform) => {
    const rank = savedScore?.rank || '';
    const scoreText = `I scored ${Math.round(score)} points${rank ? ` (Rank #${rank})` : ''} on today's JigSaw Blocks Daily Challenge! Can you beat me?`;
    const productionUrl = getProductionUrl();
    
    // Get today's date in Eastern Time for the share page
    const dateStr = getEasternDateString();
    const sharePageUrl = getDailyShareUrl(dateStr, savedScore?.player_name);
    
    if (platform === 'messenger') {
      handleMessengerShare(sharePageUrl);
    } else if (platform === 'instagram') {
      // Download image and copy text for Instagram
      const imageUrl = getDailyShareImageUrl(dateStr, savedScore?.player_name);
      handleInstagramShare(
        imageUrl,
        `${scoreText}\n${productionUrl}`,
        () => {
          toast.success('Copied! Open Instagram and paste in your story or post');
        },
        () => {
          toast.error('Failed to download image');
        }
      );
    } else if (platform === 'facebook') {
      // Use production share page URL for Facebook - it has OG meta tags with score card image
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sharePageUrl)}`,
        '_blank',
        'width=600,height=400'
      );
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(`${scoreText}\n${productionUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleDone = () => {
    if (savedScore) {
      onSaveAndShare(savedScore);
    }
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md w-[calc(var(--vvw,100vw)-1rem)] max-w-[calc(var(--vvw,100vw)-1rem)] p-0 overflow-y-auto overflow-x-hidden border-2 max-h-[calc(var(--vvh,100vh)*0.95)] sm:max-h-[calc(var(--vvh,100vh)*0.9)] box-border" 
        hideCloseButton
      >
        {step === 'congratulations' ? (
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 relative">
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => { handleDone(); onNewPuzzle?.(); }}
              className="absolute top-2 right-2 h-8 w-8 z-10"
              data-testid="daily-completion-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            {/* Header */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-primary to-accent mb-3 sm:mb-4">
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground" />
              </div>
              <h2 className="text-xl sm:text-2xl font-heading font-bold">Daily Challenge Complete!</h2>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                {isPerfect ? "Perfect game! Amazing!" : "Great job!"}
              </p>
            </div>
            
            {/* Score Display */}
            <div className="text-center py-3 sm:py-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20">
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                <div>
                  <div className="text-3xl sm:text-4xl font-heading font-bold">{Math.round(score)}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Points</div>
                </div>
                {isPerfect && <Star className="h-5 w-5 sm:h-6 sm:w-6 text-warning fill-warning" />}
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex justify-center gap-4 sm:gap-6">
              <div className="flex items-center gap-1 sm:gap-2 text-muted-foreground">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="font-semibold text-foreground text-sm sm:text-base">{formatTime(time)}</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 text-muted-foreground">
                <MousePointer2 className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="font-semibold text-foreground text-sm sm:text-base">{moves} moves</span>
              </div>
            </div>
            
            {/* Name Input */}
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Enter your name for the leaderboard</label>
                <Input
                  placeholder="Your name..."
                  value={playerName}
                  onChange={(e) => setPlayerName(sanitizeName(e.target.value))}
                  maxLength={20}
                  className="h-10 sm:h-12 text-center text-base sm:text-lg"
                  autoFocus
                />
                {localStorage.getItem('jigsaw_daily_player_name') && (
                  <p className="text-xs text-muted-foreground text-center">
                    <Flame className="h-3 w-3 inline mr-1 text-orange-500" />
                    Keep the same name to maintain your streak!
                  </p>
                )}
              </div>
              
              {submitError && (
                <div className="p-2 sm:p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm text-center">
                  {submitError}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full h-10 sm:h-12 bg-gradient-to-r from-primary to-accent text-sm sm:text-base"
                disabled={!playerName.trim() || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    Save Score
                  </>
                )}
              </Button>
            </form>
          </div>
        ) : (
          /* Share Step */
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 relative">
            {/* Close button */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => { handleDone(); onNewPuzzle?.(); }}
              className="absolute top-2 right-2 h-8 w-8 z-10"
              data-testid="daily-share-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-success/10 mb-3 sm:mb-4">
                <CheckCircle2 className="h-6 w-6 sm:h-8 sm:w-8 text-success" />
              </div>
              <h2 className="text-xl sm:text-2xl font-heading font-bold">Score Saved!</h2>
              {savedScore?.rank && (
                <p className="text-base sm:text-lg mt-2">
                  You're ranked <span className="font-bold text-primary">#{savedScore.rank}</span> today!
                </p>
              )}
            </div>
            
            {/* Streak Info */}
            {savedScore?.streak && (
              <div className="bg-orange-500/10 rounded-xl p-3 sm:p-4 border border-orange-500/20 space-y-2 sm:space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Flame className="h-5 w-5 sm:h-6 sm:w-6 text-orange-500" />
                    <div>
                      <div className="font-bold text-orange-500 text-base sm:text-lg">
                        {savedScore.streak.current} Day Streak!
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Best: {savedScore.streak.longest} days
                      </div>
                    </div>
                  </div>
                  {savedScore.streak.next_badge && (
                    <div className="text-right text-xs sm:text-sm">
                      <div className="text-muted-foreground">Next Badge</div>
                      <div className="font-medium">
                        {savedScore.streak.next_badge.emoji} in {savedScore.streak.next_badge.days_remaining}d
                      </div>
                    </div>
                  )}
                </div>
                
                {/* New Badge Earned */}
                {savedScore.streak.new_badges?.length > 0 && (
                  <div className="pt-2 sm:pt-3 border-t border-orange-500/20">
                    <div className="text-center">
                      <div className="text-xs sm:text-sm text-orange-500 font-medium mb-2">New Badge Unlocked!</div>
                      <div className="flex justify-center gap-2 flex-wrap">
                        {savedScore.streak.new_badges.map((badge) => (
                          <div
                            key={badge.id}
                            className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full bg-orange-500/20 border border-orange-500/30 text-sm"
                          >
                            <span className="text-lg sm:text-xl">{badge.emoji}</span>
                            <span className="font-medium">{badge.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="text-center py-2 sm:py-3 bg-muted/30 rounded-xl">
              <div className="text-2xl sm:text-3xl font-heading font-bold">{Math.round(score)}</div>
              <div className="text-sm text-muted-foreground">points</div>
            </div>
            
            <div className="space-y-2 sm:space-y-3">
              <p className="text-xs sm:text-sm text-muted-foreground text-center whitespace-pre-line">
                Share your achievement!{'\n'}and Challenge your Friends
              </p>
              
              {/* Share Actions: View / Facebook / Other Social Media */}
              <ChallengeShareActions
                imageUrl={savedScore?.image_url}
                difficulty="normal"
                scoreData={scoreData}
                time={time}
                moves={moves}
                deviceId={deviceId}
                playerName={savedScore?.player_name || playerName}
              />
            </div>
            
            {/* New Puzzle Button */}
            <div className="flex gap-2 sm:gap-3">
              <Button 
                className="flex-1 h-10 sm:h-12 text-sm sm:text-base bg-gradient-to-r from-primary to-accent hover:opacity-90"
                onClick={() => {
                  handleDone();
                  onNewPuzzle?.();
                }}
                data-testid="daily-new-puzzle-btn"
              >
                <Shuffle className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                New Puzzle
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
